import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMotionValueEvent, useReducedMotion, useScroll, useSpring } from "framer-motion";
import { ORIGIN } from "@/content";

// Section 01 — a reverse-chronological career ledger.
// Each <li> is a grid with three cells in DOM order: PERIOD, SPINE (decorative),
// BODY — so screen readers get year → role → title → desc → proof.
// Note: ink/NN, mute/NN and paper/NN utilities compile to nothing (the colour
// tokens have no <alpha-value>), so the rail uses inline var() styles instead.
//
// Drawn spine: as the page scrolls, the rail fills in ink from the Now node down
// to a reading line at 45% of the viewport (violet for the Now stretch). Nodes the
// line hasn't reached are hollow rings; they light in their state colour when it
// arrives, and their years go from mute to ink. Now is lit from the first paint.
// Reduced motion: rail fully drawn, every node lit, no scroll listeners.

const TRACK = { background: "var(--color-mute)", opacity: 0.2 };
const TRACK_FADE = {
  background: "linear-gradient(to bottom, transparent, var(--color-mute))",
  opacity: 0.2,
};
const PAPER_RING = "0 0 0 4px var(--color-paper)";
const HALO = { boxShadow: PAPER_RING };
const NODE_COLOUR = { ongoing: "var(--color-ink)", past: "var(--color-mute)" };
const EASE_OUT = "[transition-timing-function:cubic-bezier(0.16,1,0.3,1)]";

const READ = 0.45; // reading line, as a fraction of the viewport height
const UNLIGHT = 12; // px the line must back off above a node before it goes hollow again
// Critically damped follow: un-fills smoothly on the way up, settles in ~0.35s.
const FOLLOW = { stiffness: 260, damping: 32, restDelta: 0.0001, restSpeed: 0.001 };
const HALO_KEYFRAMES = [
  { opacity: 0.4, transform: "scale(1)" },
  { opacity: 0, transform: "scale(3.6)" },
];
const HALO_TIMING = { duration: 1000, easing: "cubic-bezier(0.2, 0.7, 0.3, 1)" };

// Keep a "·" separator from starting a wrapped line, keep a bracketed year
// ("(2012–15)") on the same line as the word before it, and keep year ranges
// ("2019–20") from splitting across lines (U+2060 word joiner after the dash).
const tidy = (s) =>
  s
    .replace(/ · /g, "\u00A0· ")
    .replace(/ \((?=\d)/g, "\u00A0(")
    .replace(/(\d)–(\d)/g, "$1–\u2060$2");

function Period({ from, to, state }) {
  return (
    <>
      <time dateTime={String(from)}>{from}</time>
      <span aria-hidden="true"> — </span>
      <span className="sr-only"> to </span>
      {to == null ? (
        <span
          className={
            state === "current"
              ? "font-semibold text-[#7C3AED] dark:text-[#A78BFA] md:inline-block md:min-w-[2.96em] md:text-left"
              : "font-semibold md:inline-block md:min-w-[2.96em] md:text-left"
          }
        >
          Now
        </span>
      ) : (
        <time dateTime={String(to)}>{to}</time>
      )}
    </>
  );
}

// Every node's centre sits 9px below the spine's padding (33px / md 41px from the
// row top), so the drawn segments can be placed in CSS and only scaled in JS.
function Node({ state, lit, live, nodeRef, haloRef }) {
  if (state === "current") {
    return (
      <span ref={nodeRef} className="relative z-[2] mt-[3px] h-3 w-3 rounded-full bg-[#8B5CF6]" style={HALO}>
        <span className="absolute -inset-[5px] rounded-full border border-[#8B5CF6]/40 animate-pulse-soft motion-reduce:animate-none" />
      </span>
    );
  }
  if (state === "education") {
    // A tick that caps the rail — no halo, so the line runs straight into it.
    // Half-drawn until the rail arrives, then full width.
    return (
      <span
        ref={nodeRef}
        className={`relative z-[2] mt-[9px] h-px w-3 ${live ? `transition-transform duration-500 ${EASE_OUT}` : ""}`}
        style={{ background: "var(--color-mute)", transform: lit ? "scaleX(1)" : "scaleX(0.5)" }}
      />
    );
  }
  // Same geometry lit or not, so only colour moves: a hollow muted ring before
  // the rail arrives, the state colour after.
  const colour = NODE_COLOUR[state] || NODE_COLOUR.past;
  const ongoing = state === "ongoing";
  return (
    <>
      {/* Halo: centred on the node but beneath the drawn rail, so the line stays
          unbroken while it ripples out from behind the paper ring. */}
      {haloRef && (
        <span
          ref={haloRef}
          className={`pointer-events-none absolute left-1/2 top-[33px] md:top-[41px] rounded-full opacity-0 ${
            ongoing ? "-ml-[5px] -mt-[5px] h-2.5 w-2.5" : "-ml-1 -mt-1 h-2 w-2"
          }`}
          style={{ background: colour }}
        />
      )}
      <span
        ref={nodeRef}
        className={`relative z-[2] ${ongoing ? "mt-[4px] h-2.5 w-2.5" : "mt-[5px] h-2 w-2"} rounded-full ${
          live ? `transition-[background-color,box-shadow] duration-500 ${EASE_OUT}` : ""
        }`}
        style={{
          background: lit ? colour : "var(--color-paper)",
          boxShadow: `inset 0 0 0 1.5px ${lit ? colour : "var(--color-mute)"}, ${PAPER_RING}`,
        }}
      />
    </>
  );
}

// Scroll driver, mounted only when motion is allowed (so reduced motion adds no
// listeners). Follows window scroll through the list, scales the drawn segments
// and reports how many nodes (from the top) the reading line has reached.
function SpineDriver({ items, listRef, nodeRefs, fillRefs, haloRefs, setLit, setLive }) {
  const { scrollYProgress } = useScroll({
    target: listRef,
    offset: [`start ${READ * 100}%`, `end ${READ * 100}%`],
  });
  const drawn = useSpring(scrollYProgress, FOLLOW);
  const geo = useRef(null);
  const shown = useRef({ lit: 0, scale: [] });
  const armed = useRef(false);

  const apply = useCallback(
    (p) => {
      const g = geo.current;
      if (!g) return;
      const y = p * g.h; // reading line, in list coordinates
      const s = shown.current;
      g.segs.forEach((seg, i) => {
        let f = (y - seg.start) / seg.len;
        f = f <= 0 ? 0 : f >= 1 ? 1 : Math.round(f * 1000) / 1000;
        const el = fillRefs.current[i];
        if (el && f !== s.scale[i]) {
          s.scale[i] = f;
          el.style.transform = `translateX(-50%) scaleY(${f})`;
        }
      });
      // Hysteresis: a node lights at its ignite point but only goes hollow again
      // once the line backs off UNLIGHT px above it, so small trackpad/touch
      // jiggles at the threshold don't re-ping the halo or flip the year colour.
      let lit = s.lit;
      while (lit < items.length && (items[lit].state === "current" || y >= g.ignite[lit])) lit++;
      while (lit > 0 && items[lit - 1].state !== "current" && y < g.ignite[lit - 1] - UNLIGHT) lit--;
      if (lit !== s.lit) {
        // One soft halo per node lit on the way down — never on first paint.
        if (armed.current) {
          for (let i = s.lit; i < lit; i++) haloRefs.current[i]?.animate?.(HALO_KEYFRAMES, HALO_TIMING);
        }
        s.lit = lit;
        setLit(lit);
      }
    },
    [items, fillRefs, haloRefs, setLit],
  );

  useMotionValueEvent(drawn, "change", apply);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    let dead = false;
    let r1 = 0;
    let r2 = 0;
    // Geometry in list coordinates — read on mount, resize and font load only.
    const measure = () => {
      const lr = list.getBoundingClientRect();
      const pts = nodeRefs.current.map((nd) => {
        const r = nd ? nd.getBoundingClientRect() : { top: lr.top, height: 0 };
        return { c: r.top + r.height / 2 - lr.top, r: Math.max(r.height, 8) / 2 };
      });
      geo.current = {
        h: list.clientHeight || 1,
        segs: pts.slice(0, -1).map((pt, i) => ({ start: pt.c, len: Math.max(1, pts[i + 1].c - pt.c) })),
        // light a node as the line meets its paper ring (radius + 4px)
        ignite: pts.map((pt) => pt.c - pt.r - 4),
      };
      return lr;
    };
    const sync = () => {
      if (dead) return;
      measure();
      shown.current.scale = [];
      apply(drawn.get());
    };

    // First paint lands in its resolved state: jump to the current reading position.
    const lr = measure();
    const vh = document.documentElement.clientHeight || window.innerHeight;
    const p0 = Math.min(1, Math.max(0, (vh * READ - lr.top) / geo.current.h));
    drawn.jump(p0);
    apply(p0);

    const ro = new ResizeObserver(sync);
    ro.observe(list);
    if (document.fonts?.ready) document.fonts.ready.then(sync);
    // Let that frame paint without transitions, then allow them (and halos).
    r1 = requestAnimationFrame(() => {
      r2 = requestAnimationFrame(() => {
        if (dead) return;
        armed.current = true;
        setLive(true);
      });
    });
    return () => {
      dead = true;
      armed.current = false;
      cancelAnimationFrame(r1);
      cancelAnimationFrame(r2);
      ro.disconnect();
    };
  }, [listRef, nodeRefs, drawn, apply, setLive]);

  return null;
}

function Chip({ label, status }) {
  return (
    <li className="inline-flex max-w-full items-center gap-2 rounded-full border border-black/10 bg-paper px-2.5 md:px-3 py-1.5 font-mono text-[10px] leading-[14px] tracking-[0.18em] uppercase text-ink whitespace-normal">
      {status === "live" && (
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500 animate-pulse-soft motion-reduce:animate-none"
        />
      )}
      {status === "testing" && (
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 shrink-0 rounded-full border"
          style={{ borderColor: "var(--color-mute)" }}
        />
      )}
      {label}
    </li>
  );
}

function Proof({ item }) {
  const chips = item.chips || [];
  if (!chips.length && !item.link) return null;
  return (
    <ul role="list" className="mt-5 lg:-mt-[5px] flex flex-wrap lg:flex-col lg:items-start gap-1.5 md:gap-2">
      {chips.map((c) => (
        <Chip key={c.label} {...c} />
      ))}
      {item.link && (
        <li className="inline-flex">
          <Link
            to={item.link}
            aria-label={`${item.org} case study`}
            className="inline-flex items-center gap-1.5 rounded-full bg-ink px-2.5 md:px-3 py-1.5 font-mono text-[10px] leading-[14px] tracking-[0.18em] uppercase text-paper transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
          >
            Case study →
          </Link>
        </li>
      )}
    </ul>
  );
}

function Body({ item }) {
  if (item.state === "education") {
    return (
      <div>
        <p className="font-mono text-[11px] md:text-[12px] leading-[18px] tracking-[0.14em] uppercase text-mute">
          <span className="sr-only">{item.role}: </span>
          {[...item.lines, item.org].map((line, i) => (
            <React.Fragment key={line}>
              {i > 0 && <br />}
              {tidy(line)}
            </React.Fragment>
          ))}
        </p>
        <p className="mt-2 font-serif italic text-[18px] text-mute">{item.desc}</p>
      </div>
    );
  }

  const current = item.state === "current";
  return (
    <>
      <div>
        <p className="label-mono leading-[18px] mb-3">
          {tidy(item.role)}
          <span className="hidden md:inline">{"\u00A0· "}</span>
          <br className="md:hidden" />
          <span className="whitespace-nowrap">{item.org}</span>
        </p>
        <h3
          className={`font-serif text-[28px] leading-[1.08] tracking-tight text-ink text-balance ${
            current ? "md:text-[40px] lg:text-[46px]" : "md:text-[34px] lg:text-[38px]"
          }`}
        >
          {item.title}
        </h3>
        <p className="mt-3 max-w-[56ch] text-[15px] md:text-[16px] leading-[1.6] text-mute">
          {tidy(item.desc)}
        </p>
      </div>
      <Proof item={item} />
    </>
  );
}

export default function Origin() {
  const reduce = useReducedMotion();
  const { timeline, meta } = ORIGIN;
  const lastIndex = timeline.length - 1;
  // How many nodes, from the top, the reading line has reached (Now is always lit).
  const [lit, setLit] = useState(reduce ? timeline.length : 0);
  // Transitions stay off until the first frame has painted in its resolved state.
  const [live, setLive] = useState(false);
  const listRef = useRef(null);
  const nodeRefs = useRef([]);
  const fillRefs = useRef([]);
  const haloRefs = useRef([]);

  return (
    <section
      id="origin"
      aria-labelledby="origin-title"
      className="chapter relative py-24 md:py-36"
      data-testid="chapter-origin"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        {/* Header — standfirst on the left, demoted portrait (lg+) on the right */}
        <div className="mb-12 md:mb-16 lg:grid lg:grid-cols-[minmax(0,1fr)_264px] lg:gap-16 lg:items-end">
          <div>
            <div className="label-mono mb-5">{ORIGIN.kicker}</div>
            <h2
              id="origin-title"
              className="font-serif text-[44px] md:text-[72px] leading-[1.0] tracking-tight text-ink"
            >
              {ORIGIN.title}
              <br />
              <span className="italic text-mute">{ORIGIN.titleEm}</span>
            </h2>
            <p className="mt-6 max-w-[56ch] text-[17px] md:text-[19px] leading-[1.55] text-mute text-pretty">
              {ORIGIN.intro}
            </p>
            <p className="mt-5 font-mono text-[11px] tracking-[0.18em] uppercase text-mute">
              {meta.location} ·{" "}
              <a
                href={meta.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Sam Erpik on LinkedIn (opens in a new tab)"
                className="text-ink underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
              >
                LinkedIn ↗
              </a>
            </p>
          </div>

          <figure className="hidden lg:block w-[220px] m-0" data-testid="origin-portrait">
            <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-black/10">
              <picture>
                <source
                  type="image/webp"
                  srcSet="/images/sam-quantumeye-sm.webp"
                  media="(max-width: 768px)"
                />
                <source type="image/webp" srcSet="/images/sam-quantumeye.webp" />
                <img
                  src="/images/sam-quantumeye.jpg"
                  alt="Sam Erpik at the QuantumEye booth, Retail Tech Show 2025"
                  loading="lazy"
                  className="w-full h-full object-cover transition-[filter] duration-700 [filter:grayscale(100%)_sepia(14%)_contrast(1.05)_brightness(1.02)] hover:[filter:none]"
                  style={{ objectPosition: "32% 30%" }}
                />
              </picture>
            </div>
            <figcaption className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-mute">
              Retail Tech Show 2025
              <br />
              QuantumEye stand
            </figcaption>
          </figure>
        </div>

        {/* Ledger */}
        <ol ref={listRef} role="list" className="relative m-0 p-0 list-none">
          {timeline.map((item, i) => {
            const first = i === 0;
            const last = i === lastIndex;
            const bodyPad =
              item.state === "current"
                ? "pb-8 md:pb-12"
                : item.state === "education"
                  ? "pb-0 md:pb-0"
                  : "pb-8 md:pb-10";

            const on = reduce || i < lit || item.state === "current";

            return (
              <li
                key={`${item.org}-${item.from}`}
                className="grid grid-cols-[16px_minmax(0,1fr)] gap-x-4 md:grid-cols-[112px_24px_minmax(0,1fr)] md:gap-x-6 lg:grid-cols-[128px_24px_minmax(0,1fr)] lg:gap-x-8"
                data-testid={`origin-item-${i}`}
              >
                {/* (1) PERIOD */}
                <p
                  className={`col-start-2 row-start-1 md:col-start-1 border-t border-black/10 pt-6 md:border-t-0 md:pt-8 md:text-right font-mono text-[12px] md:text-[13px] leading-[18px] tracking-[0.14em] uppercase tabular-nums ${
                    on ? "text-ink" : "text-mute"
                  } ${live ? `transition-colors duration-500 ${EASE_OUT}` : ""}`}
                >
                  <Period from={item.from} to={item.to} state={item.state} />
                </p>

                {/* (2) SPINE — decorative axis + node */}
                <div
                  aria-hidden="true"
                  className="relative col-start-1 row-start-1 row-span-2 md:col-start-2 md:row-span-1 flex justify-center items-start pt-6 md:pt-8"
                >
                  {first && (
                    <span
                      className="absolute -top-12 h-12 left-1/2 w-px -translate-x-1/2"
                      style={TRACK_FADE}
                    />
                  )}
                  <span
                    className={`absolute left-1/2 w-px -translate-x-1/2 ${
                      last ? "top-0 h-[33px] md:h-[41px]" : "inset-y-0"
                    }`}
                    style={TRACK}
                  />
                  {/* Drawn segment: this node's centre to the next one's, so colour
                      changes hide inside the next node's paper ring. */}
                  {!last && (
                    <span
                      ref={(el) => {
                        fillRefs.current[i] = el;
                      }}
                      className={`absolute left-1/2 z-[1] w-px origin-top -translate-x-1/2 top-[33px] -bottom-[33px] md:top-[41px] md:-bottom-[41px] ${
                        reduce ? "" : "scale-y-0 will-change-transform"
                      } ${item.state === "current" ? "bg-[#7C3AED] dark:bg-[#A78BFA]" : ""}`}
                      style={item.state === "current" ? undefined : { background: "var(--color-ink)" }}
                    />
                  )}
                  <Node
                    state={item.state}
                    lit={on}
                    live={live}
                    nodeRef={(el) => {
                      nodeRefs.current[i] = el;
                    }}
                    haloRef={
                      reduce
                        ? undefined
                        : (el) => {
                            haloRefs.current[i] = el;
                          }
                    }
                  />
                </div>

                {/* (3) BODY */}
                <div
                  className={`col-start-2 row-start-2 md:col-start-3 md:row-start-1 pt-2 md:pt-8 ${bodyPad} border-black/10 md:border-t lg:grid lg:grid-cols-[minmax(0,1fr)_264px] lg:gap-x-8`}
                >
                  <Body item={item} />
                </div>
              </li>
            );
          })}
        </ol>
        {!reduce && (
          <SpineDriver
            items={timeline}
            listRef={listRef}
            nodeRefs={nodeRefs}
            fillRefs={fillRefs}
            haloRefs={haloRefs}
            setLit={setLit}
            setLive={setLive}
          />
        )}
      </div>
    </section>
  );
}
